import React from 'react';
import { Link } from 'react-router-dom';
import { BiMessageSquareDots } from 'react-icons/bi';
import { PiNotification } from 'react-icons/pi';
import { MdLightMode, MdDarkMode } from 'react-icons/md';
import MessagingDropdown from '../../../../MainHeader/Messaging/Messaging';
import NotificationsDropdown from '../../../../MainHeader/Notifications/Notifications';
import MyProfileDropdown from '../../../../MainHeader/Profile/MyProfile';
import { useNotificationUnreadCount } from '../../../../notifications/useNotificationUnreadCount';
import { useMessagingUnreadCount } from '../../../../MainHeader/Messaging/useMessagingUnreadCount';
import { applyProfileAvatarImgFallback } from '../../../../MainHeader/profilePictureUrl';
import { buildSigninUrl, GUEST_CREATE_ACCOUNT_LABEL, GUEST_SIGN_IN_LABEL } from '../../auth/guestAuth';

/**
 * Posts shell header right: theme + guest sign-up or signed-in tools (messages, notifications, profile).
 */
export default function PostsShellHeaderAuth({
  isGuestViewer,
  returnTo,
  onGuestSignUp,
  onGuestSignIn,
  themeMode,
  onToggleTheme,
  headerDropdowns,
  onToggleHeaderDropdown,
  messagingRef,
  notificationsRef,
  profileRef,
  profileAvatarSrc,
  profileUserId = '',
  profileDisplayName = '',
  profileMode = 'personal',
  baseUrl,
  onNotificationsNavigateAll,
  onManageProfileClick,
  messagesStandalone = false,
}) {
  const rt = returnTo || '/posts';
  const { hasUnread, refreshUnreadCount } = useNotificationUnreadCount(baseUrl);
  const { hasUnread: hasUnreadMessages, refreshUnreadCount: refreshMessagingUnread } =
    useMessagingUnreadCount(baseUrl);

  return (
    <>
      {!isGuestViewer && !messagesStandalone ? (
        <>
          <div
            className={`support-open-disc-shell-header-icon noOutline-text${headerDropdowns.messaging ? ' is-active' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="Messaging"
            ref={messagingRef}
            onClick={() => {
              refreshMessagingUnread();
              onToggleHeaderDropdown('messaging');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                refreshMessagingUnread();
                onToggleHeaderDropdown('messaging');
              }
            }}
          >
            <span className="support-open-disc-shell-notifications-wrap">
              <BiMessageSquareDots aria-hidden />
              {hasUnreadMessages ? (
                <span
                  className="support-open-disc-shell-notifications-dot"
                  aria-label="Unread messages"
                />
              ) : null}
            </span>
            {headerDropdowns.messaging ? (
              <MessagingDropdown
                anchorRef={messagingRef}
                onNavigateToAll={() => onToggleHeaderDropdown('messaging')}
              />
            ) : null}
          </div>
          <div
            className={`support-open-disc-shell-header-icon noOutline-text${headerDropdowns.notifications ? ' is-active' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="Notifications"
            ref={notificationsRef}
            onClick={() => {
              refreshUnreadCount();
              onToggleHeaderDropdown('notifications');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onToggleHeaderDropdown('notifications');
            }}
          >
            <span className="support-open-disc-shell-notifications-wrap">
              <PiNotification aria-hidden />
              {hasUnread ? (
                <span
                  className="support-open-disc-shell-notifications-dot"
                  aria-label="Unread notifications"
                />
              ) : null}
            </span>
            {headerDropdowns.notifications ? (
              <NotificationsDropdown
                anchorRef={notificationsRef}
                baseUrl={baseUrl}
                profileMode={profileMode}
                onMarkRead={refreshUnreadCount}
                onNavigateToAll={onNotificationsNavigateAll}
              />
            ) : null}
          </div>
        </>
      ) : null}
      <button
        type="button"
        className="support-open-disc-shell-header-btn support-open-disc-shell-header-theme noOutline-text"
        aria-label="Toggle theme"
        onClick={onToggleTheme}
      >
        {themeMode === 'light' ? (
          <MdLightMode className="support-open-disc-shell-theme-icon" aria-hidden />
        ) : (
          <MdDarkMode className="support-open-disc-shell-theme-icon" aria-hidden />
        )}
      </button>
      {isGuestViewer ? (
        <div className="od-guest-shell-header-auth">
          <Link to={buildSigninUrl({ returnTo: rt })} className="od-guest-shell-header-auth__signin">
            {GUEST_SIGN_IN_LABEL}
          </Link>
          <button
            type="button"
            className="od-guest-shell-create-account-btn"
            onClick={onGuestSignUp}
          >
            {GUEST_CREATE_ACCOUNT_LABEL}
          </button>
        </div>
      ) : (
        <div
          className={`support-open-disc-shell-header-profile noOutline-text${headerDropdowns.profile ? ' is-active' : ''}`}
          role="button"
          tabIndex={0}
          aria-label="Profile"
          ref={profileRef}
          onClick={() => onToggleHeaderDropdown('profile')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onToggleHeaderDropdown('profile');
          }}
        >
          <img
            src={profileAvatarSrc}
            alt="Profile"
            className="support-open-disc-shell-header-avatar"
            onError={(e) => {
              applyProfileAvatarImgFallback(e.currentTarget, {
                userId: profileUserId,
                name: profileDisplayName,
              });
            }}
          />
          {headerDropdowns.profile ? (
            <MyProfileDropdown
              guestBrowse={false}
              onManageProfileClick={onManageProfileClick}
              accountOnly={messagesStandalone}
            />
          ) : null}
        </div>
      )}
    </>
  );
}
